import { CashTransactionType } from '@/features/graphql/generated/graphql'
import { useHandleQueryErrorWithToast } from '@/features/shiftCloseouts/hooks/useHandleQueryErrorWithToast'
import { logger } from '@/features/shiftCloseouts/logger'
import { cashTransactionCreateMutation } from '@/features/shiftCloseouts/mutations'
import { CashTransactionsQuery, ShiftCloseoutPreviewQuery } from '@/features/shiftCloseouts/queries'
import { CashTransactionDraft, CashTransactionItem } from '@/features/shiftCloseouts/types'
import { CashTransactionTotals } from '@/features/shiftCloseouts/utils/calculateCashTransactionTotals'
import { isValidCashTransactionAmount } from '@/features/shiftCloseouts/utils/isValidCashTransactionAmount'
import { parseMoneyInput } from '@/features/shiftCloseouts/utils/moneyInput'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { ApolloError, useMutation, useQuery } from '@apollo/client'
import currencyJs from 'currency.js'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface UseCashTransactionsResult {
    currency?: string
    deposit: CashTransactionDraft
    error?: ApolloError
    loading: boolean
    totals?: CashTransactionTotals
    transactions: CashTransactionItem[]
    withdrawal: CashTransactionDraft
}

export const useCashTransactions = (): UseCashTransactionsResult => {
    const { t } = useTranslation()
    const defaultDepositNote = t('shiftCloseouts.cashTransactions.deposit')
    const defaultWithdrawalNote = t('shiftCloseouts.cashTransactions.withdrawal')
    const requestInFlightRef = useRef(false)
    const handleError = useHandleErrorWithToast(logger)
    const {
        data,
        error,
        loading: queryLoading,
    } = useQuery(CashTransactionsQuery, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    })
    const [createTransaction, { loading: createLoading }] = useMutation(
        cashTransactionCreateMutation,
        {
            awaitRefetchQueries: true,
            onError: handleError,
            refetchQueries: [CashTransactionsQuery, ShiftCloseoutPreviewQuery],
        }
    )
    const [depositAmount, setDepositAmount] = useState('0')
    const [depositNote, setDepositNote] = useState('')
    const [withdrawalAmount, setWithdrawalAmount] = useState('0')
    const [withdrawalNote, setWithdrawalNote] = useState('')
    const loading = queryLoading || createLoading

    useHandleQueryErrorWithToast(error, queryLoading)

    const transactions = useMemo<CashTransactionItem[]>(
        () =>
            data?.cashTransactions.map((transaction) => ({
                amount: transaction.amount,
                id: transaction.id,
                note:
                    transaction.note ??
                    (transaction.type === CashTransactionType.Deposit
                        ? defaultDepositNote
                        : defaultWithdrawalNote),
                transactedAt: transaction.transactedAt,
                type: transaction.type,
            })) ?? [],
        [data?.cashTransactions, defaultDepositNote, defaultWithdrawalNote]
    )

    const addTransaction = async (
        type: CashTransactionType,
        amountInput: string,
        noteInput: string,
        defaultNote: string,
        reset: () => void
    ) => {
        if (requestInFlightRef.current) return

        const amount = parseMoneyInput(amountInput)
        const note = noteInput.trim() || defaultNote

        if (!amount || currencyJs(amount).value <= 0) return

        requestInFlightRef.current = true

        try {
            const { data: mutationData } = await createTransaction({
                variables: { transaction: { amount, note, type } },
            })

            if (mutationData?.cashTransactionCreate) reset()
        } finally {
            requestInFlightRef.current = false
        }
    }

    return {
        currency: data?.cashRegisterBalance.currency,
        deposit: {
            amount: depositAmount,
            canSubmit: !loading && isValidCashTransactionAmount(depositAmount),
            note: depositNote,
            onAmountChange: setDepositAmount,
            onNoteChange: setDepositNote,
            onSubmit: () => {
                void addTransaction(
                    CashTransactionType.Deposit,
                    depositAmount,
                    depositNote,
                    defaultDepositNote,
                    () => {
                        setDepositAmount('0')
                        setDepositNote('')
                    }
                )
            },
        },
        error,
        loading,
        totals: data
            ? {
                  deposits: data.cashTransactionTotals.deposits,
                  total: data.cashTransactionTotals.difference,
                  withdrawals: data.cashTransactionTotals.withdrawals,
              }
            : undefined,
        transactions,
        withdrawal: {
            amount: withdrawalAmount,
            canSubmit: !loading && isValidCashTransactionAmount(withdrawalAmount),
            note: withdrawalNote,
            onAmountChange: setWithdrawalAmount,
            onNoteChange: setWithdrawalNote,
            onSubmit: () => {
                void addTransaction(
                    CashTransactionType.Withdrawal,
                    withdrawalAmount,
                    withdrawalNote,
                    defaultWithdrawalNote,
                    () => {
                        setWithdrawalAmount('0')
                        setWithdrawalNote('')
                    }
                )
            },
        },
    }
}
